import { useCallback } from 'react';

import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFormContext, useWatch } from 'react-hook-form';

import type { GetPropertyTypesQuery } from '@graphql/__generated__/graphql';
import { usePropertyTypeOptions } from '@modules/_shared/components/create-transaction/hooks/usePropertyTypeOptions';
import type { TransactionFormData } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { RaNumber } from '@modules/_shared/components/form/RaNumber';
import { RaSelect } from '@modules/_shared/components/form/RaSelect';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { useTransactionPropertyTypeSelection } from '@modules/_shared/hooks/useTransactionPropertyTypeSelection';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  clearInvalidTransactionPropertyDetailFields,
  isTransactionPropertyDetailFieldVisible,
} from '@modules/_shared/utils/transactionPropertyDetails';
import { useLocale } from '@shared/hooks/locale';

import { isSpanishTransactionCountry } from '../transactionCountry';
import { TransactionStepShell } from '../TransactionStepShell';

type PropertyType = GetPropertyTypesQuery['property_types'][number];

type PropertyStepProps = {
  countryCode?: string | null;
};

export const PropertyStep: React.FC<PropertyStepProps> = ({ countryCode }) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { control, setValue, resetField } =
    useFormContext<TransactionFormData>();

  const compositeType = useWatch<
    TransactionFormData,
    'property.composite_type'
  >({
    name: 'property.composite_type',
  });
  const propertyTypeId = useWatch<
    TransactionFormData,
    'property.property_type_id'
  >({
    name: 'property.property_type_id',
  });

  const { mainTypeOptions, subTypeOptions, loading, propertyTypes } =
    usePropertyTypeOptions(compositeType);

  const handleInvalidSelection = useCallback(() => {
    resetField('property.property_type_id', { defaultValue: '' });
  }, [resetField]);

  const handleSelectedPropertyType = useCallback(
    (propertyType: PropertyType | undefined) => {
      if (propertyType == null) {
        return;
      }

      clearInvalidTransactionPropertyDetailFields(propertyType, field => {
        setValue(`property.${field}`, undefined);
      });
    },
    [setValue],
  );

  useTransactionPropertyTypeSelection({
    propertyTypes,
    propertyTypeId,
    compositeType,
    onInvalidSelection: handleInvalidSelection,
    onSelectedPropertyType: handleSelectedPropertyType,
  });

  const isSpanish = isSpanishTransactionCountry(
    countryCode,
    me?.tenant.country_code,
  );
  const hasPropertyTypeSelected = compositeType && propertyTypeId;

  const propertyType = propertyTypes?.find(pt => pt.id === propertyTypeId);
  const roomsField = isSpanish ? 'number_of_bedrooms' : 'number_of_rooms';
  const surfaceField = isSpanish ? 'built_surface' : 'living_surface';
  const showRoomsField = isTransactionPropertyDetailFieldVisible(
    propertyType,
    roomsField,
  );
  const showSurfaceField = isTransactionPropertyDetailFieldVisible(
    propertyType,
    surfaceField,
  );

  if (loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <TransactionStepShell title={t('Property')} withFieldsGrid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <RaSelect
          name="property.composite_type"
          label={t('Main type')}
          control={control}
          options={mainTypeOptions}
          required
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <RaSelect
          name="property.property_type_id"
          label={t('Type')}
          control={control}
          options={subTypeOptions}
          required
        />
      </Grid>
      {hasPropertyTypeSelected && (
        <>
          {showRoomsField && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <RaNumber
                name={
                  isSpanish
                    ? 'property.number_of_bedrooms'
                    : 'property.number_of_rooms'
                }
                label={
                  isSpanish ? t('Number of bedrooms') : t('Number of rooms')
                }
                control={control}
                decimalNumbers={isSpanish ? 0 : 1}
                min={1}
                required
              />
            </Grid>
          )}

          {showSurfaceField && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <RaNumber
                name={
                  isSpanish
                    ? 'property.built_surface'
                    : 'property.living_surface'
                }
                label={
                  isSpanish ? t('Built surface (m²)') : t('Living Surface (m²)')
                }
                control={control}
                decimalNumbers={0}
                min={1}
                suffix=" m²"
                required
              />
            </Grid>
          )}

          {isTransactionPropertyDetailFieldVisible(
            propertyType,
            'usable_surface',
          ) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <RaNumber
                name="property.usable_surface"
                label={t('Usable surface (m²)')}
                control={control}
                decimalNumbers={0}
                min={1}
                suffix=" m²"
                required
              />
            </Grid>
          )}

          {isTransactionPropertyDetailFieldVisible(
            propertyType,
            'land_surface',
          ) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <RaNumber
                name="property.land_surface"
                label={t('Land Surface (m²)')}
                control={control}
                decimalNumbers={0}
                min={1}
                suffix=" m²"
                required
              />
            </Grid>
          )}
        </>
      )}
    </TransactionStepShell>
  );
};
